import { AUTH_URL, SERVER_MCP_TODO_URL } from './settings';
import { useState, useEffect } from 'react';
import './App.css';

function App() {
	const [user, setUser] = useState({});
	const [userQuery, setUserQuery] = useState('');
	const [responses, setResponses] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [logs, setLogs] = useState([]);
	const [isConnected, setIsConnected] = useState(false);
	const [isAuthenticated, setIsAuthenticated] = useState(false);

	// Fonction pour ajouter un log
	const addLog = (message) => {
		const timestamp = new Date().toLocaleTimeString('fr-FR');
		setLogs((prevLogs) => [
			{
				id: Date.now(),
				message,
				timestamp,
			},
			...prevLogs,
		]);
	};

	// Vérifier l'authentification au chargement
	useEffect(() => {
		checkAuth();
	}, []);

	// Vérifier si l'utilisateur est authentifié
	const checkAuth = async () => {
		try {
			// Vérifier si les cookies sont présents (authentification)
			const response = await fetch(`${AUTH_URL}/me`, {
				credentials: 'include',
			});

			if (response.ok) {
				const user = await response.json();
				setIsAuthenticated(true);
				setUser(user);
				addLog('✅ Utilisateur authentifié');
			}
		} catch (err) {
			setIsAuthenticated(false);
		}
	};

	// Déconnexion
	const handleLogout = async () => {
		try {
			const logout = await fetch(`${AUTH_URL}/logout`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				credentials: 'include',
			});
			console.log('LOGOUT', logout);
		} catch (error) {
			console.log('error LOGOUT', error);
			addLog('error LOGOUT', error);
		}
		setIsAuthenticated(false);
		setEmail('');
		setPassword('');
		addLog('🔓 Déconnexion');
	};

	// Connexion au client-mcp
	const connectToMCP = async () => {
		try {
			addLog('Tentative de connexion au client MCP...');
			const response = await fetch(`/api/connect`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					serverUrl: `${SERVER_MCP_TODO_URL}/mcp`,
				}),
			});

			if (!response.ok) {
				const errorData = await response.json();
				addLog(`Erreur connexion: ${JSON.stringify(errorData)}`);
				throw new Error(
					errorData.error || `HTTP error! status: ${response.status}`,
				);
			}

			const data = await response.json();
			console.log({ data });
			if (data.success) {
				setIsConnected(true);
				addLog('✅ Connecté au serveur MCP');
				addLog(`Outils disponibles: ${data.tools?.length || 0}`);
			}

			return data;
		} catch (err) {
			console.error('Erreur connexion MCP:', err);
			addLog(`❌ Erreur connexion: ${err.message}`);
			setIsConnected(false);
			throw err;
		}
	};

	// Envoyer une requête utilisateur -> backend app -> client LLM -> serveur MCP
	const sendQuery = async (query) => {
		try {
			addLog(`📤 Envoi de la requête: "${query.substring(0, 50)}..."`);

			const userQuery =
				'utilisateur : ' + JSON.stringify(user) + '| message: ' + query;
			const response = await fetch(`/api/query`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ query: userQuery }),
			});

			if (!response.ok) {
				const errorData = await response.json();
				addLog(`Erreur requête: ${JSON.stringify(errorData)}`);
				throw new Error(
					errorData.error || `HTTP error! status: ${response.status}`,
				);
			}

			const data = await response.json();

			if (data.success) {
				addLog('✅ Réponse reçue du LLM');
				return data.response;
			} else {
				throw new Error(data.error || 'Erreur inconnue');
			}
		} catch (err) {
			console.error('Erreur requête:', err);
			addLog(`❌ Erreur requête: ${err.message}`);
			throw err;
		}
	};

	// Vérifier le statut de connexion
	const checkStatus = async () => {
		try {
			const response = await fetch(`/api/status`);
			const data = await response.json();

			console.log({ data });
			if (data.success) {
				setIsConnected(data.connected);
				addLog(
					`Statut: ${
						data.connected ? 'Connecté' : 'Déconnecté'
					} - Outils: ${data.toolsCount || 0}`,
				);
			}

			return data;
		} catch (err) {
			console.error('Erreur statut:', err);
			addLog(`❌ Erreur vérification statut: ${err.message}`);
			setIsConnected(false);
		}
	};

	// Gérer les logs
	const handleEmptyLogs = () => {
		setLogs([]);
	};
	// Gérer la soumission du formulaire
	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!userQuery.trim()) return;

		setLoading(true);
		setError(null);

		try {
			// Vérifier la connexion
			if (!isConnected) {
				addLog('🔌 Connexion au serveur MCP nécessaire...');
				await connectToMCP();
			}

			// Envoyer la requête
			const llmResponse = await sendQuery(userQuery);

			// Ajouter la nouvelle réponse en haut de la liste
			setResponses((prevResponses) => [
				{
					id: Date.now(),
					query: userQuery,
					response: llmResponse,
					timestamp: new Date().toLocaleTimeString('fr-FR'),
				},
				...prevResponses,
			]);

			// Réinitialiser le champ de saisie
			setUserQuery('');
		} catch (err) {
			setError(err.message);
		} finally {
			setLoading(false);
		}
	};

	// Affichage du formulaire de login si non authentifié
	if (!isAuthenticated) {
		return (
			<div className="App">
				<header className="App-header">
					<h1>🔐 Connexion</h1>
					<p>
						<span style={{ marginRight: 20 }}>
							Authentifiez-vous pour accéder à l'interface MCP
						</span>
						<a href={`${AUTH_URL}/?app=chat`}>
							<button className="login-button">Login</button>
						</a>
					</p>
				</header>
			</div>
		);
	}

	return (
		<div className="App">
			<header className="App-header">
				<h1>🤖 Interface MCP - Claude LLM</h1>
				<p>
					Posez vos questions au LLM Claude avec les outils du serveur
					MCP-Todos
				</p>
				<div className="header-info">
					<div className="connection-status">
						{isConnected ? '🟢 Connecté MCP' : '🔴 Déconnecté MCP'}
					</div>
					<button onClick={handleLogout} className="logout-button">
						🔓 Déconnexion
					</button>
				</div>
			</header>

			<main className="main-content">
				<div className="query-container">
					{/* Formulaire de saisie */}
					<form onSubmit={handleSubmit} className="query-form">
						<textarea
							value={userQuery}
							onChange={(e) => setUserQuery(e.target.value)}
							placeholder="Posez votre question ici..."
							disabled={loading}
							className="query-input"
							rows={4}
						/>
						<div className="form-actions">
							<button
								type="submit"
								disabled={loading || !userQuery.trim()}
								className="submit-button"
							>
								{loading ? '⏳ Traitement...' : '🚀 Envoyer'}
							</button>
							<button
								type="button"
								onClick={() => {
									setUserQuery('');
									setResponses([]);
									setError(null);
								}}
								disabled={loading}
								className="clear-button"
							>
								🗑️ Effacer tout
							</button>
						</div>
					</form>

					{/* Messages d'erreur */}
					{error && (
						<div className="error-message">
							❌ {error}
							<button
								onClick={() => setError(null)}
								className="close-button"
							>
								×
							</button>
						</div>
					)}

					{/* Liste des réponses - la plus récente en haut */}
					{responses.length > 0 && (
						<div className="responses-list">
							{responses.map((item) => (
								<div key={item.id} className="response-container">
									<div className="response-header">
										<h3>💬 Réponse de Claude</h3>
										<span className="response-timestamp">
											[{item.timestamp}]
										</span>
									</div>
									<div className="response-query">
										<strong>Question :</strong> {item.query}
									</div>
									<div className="response-content">
										{item.response}
									</div>
								</div>
							))}
						</div>
					)}

					{/* Actions */}
					<div className="actions">
						<button
							onClick={connectToMCP}
							disabled={loading || isConnected}
							className="action-button"
						>
							🔌 {isConnected ? 'Déjà connecté' : 'Se connecter'}
						</button>
						<button
							onClick={checkStatus}
							disabled={loading}
							className="action-button"
						>
							📊 Vérifier le statut
						</button>
					</div>
				</div>

				{/* Panneau de logs */}
				<div className="logs-panel">
					<div className="logs-header">
						<h3>📋 Logs</h3>
						<button
							onClick={handleEmptyLogs}
							className="clear-logs-button"
							title="Effacer les logs"
						>
							🗑️
						</button>
					</div>
					<div id="logs" className="logs-content">
						{logs.length === 0 ? (
							<div className="empty-logs">
								Aucun log pour le moment
							</div>
						) : (
							logs.map((log) => (
								<div key={log.id} className="log-entry">
									<span className="log-timestamp">
										[{log.timestamp}]
									</span>
									<span className="log-message">
										{log.message}
									</span>
								</div>
							))
						)}
					</div>
				</div>
			</main>
		</div>
	);
}

export default App;
